import type {
  AvailabilityDecodeResult,
  AvailabilityObservation,
} from '../availability/availability';
import type {
  AvailabilityReadResult,
  AvailabilityRepository,
} from '../availability/availability-repository';
import type { CategoryNodeMap } from '../category/category-node-map';
import type { SegmentEvaluatorService } from '../segment/segment-evaluator.service';
import type { SkuPredicate } from '../segment/sku-predicate';
import type { EAN } from '../shared/ean';
import type { Measurement, MeasurementWindow } from './measurement';

// ─── Output type ──────────────────────────────────────────────────────────────

/**
 * Per-EAN availability facts returned by evaluate() for the distribution measure.
 * The distribution family's fact is an AvailabilityObservation, not a SeriesObservation,
 * so it gets its own result kind rather than overloading partitioned-observations.
 */
export type PartitionedAvailabilityResult = {
  readonly kind: 'partitioned-availability';
  /** Maps the bare real EAN → its availability facts inside the window. */
  readonly partitions: ReadonlyMap<EAN, readonly AvailabilityObservation[]>;
  /** The feed's product name per EAN the read saw, passed through from the ACL. */
  readonly names: ReadonlyMap<EAN, string | null>;
};

// ─── Slot inventory ───────────────────────────────────────────────────────────

/**
 * The distribution family has exactly one consumed combination: sku-partitioned weekly
 * observations over an unfiltered inline brand universe. Returns the slots a measurement
 * deviates in, empty when it matches.
 */
export function distributionSlots(m: Measurement): string[] {
  const deviating: string[] = [];

  if (m.grain !== 'observations') deviating.push(`grain:${m.grain ?? 'aggregated'}`);
  if (m.partitionBy !== 'sku') deviating.push(`partitionBy:${m.partitionBy}`);
  if (m.window?.mode !== 'weekly') deviating.push(`window.mode:${m.window?.mode ?? 'absent'}`);
  if (m.shareMode !== 'absolute') deviating.push(`shareMode:${m.shareMode}`);
  if (m.show !== undefined) deviating.push('show:present');
  if (m.compare !== undefined) deviating.push('compare:present');
  if (m.universe.kind !== 'inline') deviating.push(`universe:${m.universe.kind}`);
  // The availability read takes no vendor or channel narrowing and the route never
  // post-filters, so accepting a filter here would drop it silently (D-03).
  if (m.filter.vendors !== undefined || m.filter.channels !== undefined) {
    deviating.push('filter:present');
  }
  // A region grain describes the region partition's keys and nothing else, so this family
  // carrying one would silently drop an input the caller meant to be honoured.
  if (m.regionGrain !== undefined) deviating.push('regionGrain:present');
  // The post-read region narrowing belongs to the regional family's facts, which carry a
  // region. Availability facts carry none, so the slot here is an input this route cannot
  // honour — named rather than dropped.
  if (m.filter.regions !== undefined) deviating.push('filter.regions:present');

  return deviating;
}

// ─── Route ────────────────────────────────────────────────────────────────────

/**
 * Evaluates the distribution measure into per-EAN availability facts inside the window.
 *
 * ACL boundary: AvailabilityRepository owns the feed's masked product keys and payload
 * nesting; the EANs it returns — and so these partition keys — are bare real EANs.
 */
export async function evaluateDistribution(
  availability: AvailabilityRepository,
  segments: SegmentEvaluatorService,
  measurement: Measurement,
  nodeMap: CategoryNodeMap | null
): Promise<PartitionedAvailabilityResult> {
  const read = await readAvailability(availability, segments, measurement, nodeMap);
  return {
    kind: 'partitioned-availability',
    partitions: partitionAvailabilityByEan(read.observations, measurement.window!),
    names: read.names,
  };
}

// The two inline universes the availability read serves: one brand on its own (readByBrand),
// or that brand composed with one narrowing predicate — a comparison's Segment.toPredicate —
// whose EANs resolve through the segment evaluator (readByEans, which narrows to the brand).
async function readAvailability(
  availability: AvailabilityRepository,
  segments: SegmentEvaluatorService,
  measurement: Measurement,
  nodeMap: CategoryNodeMap | null
): Promise<AvailabilityReadResult> {
  const { universe } = measurement;
  if (universe.kind !== 'inline') {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: a rollup universe (segment "${universe.id}") holds ` +
        `no availability facts; measure "distribution" needs an inline brand universe`
    );
  }
  const shape = availabilityUniverseShape(universe.predicate);
  if (shape === null) {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: measure "distribution" needs an inline universe of ` +
        `one brand ("brand-in" with a single slug), optionally composed with one narrowing ` +
        `predicate; got ${describeUniverseShape(universe.predicate)}. No other shape has a ` +
        `consumer (D-03).`
    );
  }
  if (shape.narrow === null) {
    return availability.readByBrand(shape.slug);
  }
  if (nodeMap === null) {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: nodeMap is required for measure "distribution" ` +
        `over a brand narrowed by a predicate (EAN resolution path)`
    );
  }
  const eans = await segments.evaluate(shape.narrow, nodeMap);
  return availability.readByEans([...eans], shape.slug);
}

// ─── Universe shape ───────────────────────────────────────────────────────────

type AvailabilityUniverseShape = {
  readonly slug: string;
  // null when the brand stands alone; the narrowing predicate when it is composed.
  readonly narrow: SkuPredicate | null;
};

// The inline shape an availability read serves: one brand, optionally narrowed by one
// further predicate. Returns null for every other shape — several brands, a bare category
// or tag, a composite without a single-brand arm — none of which has a consumer (D-03).
function availabilityUniverseShape(predicate: SkuPredicate): AvailabilityUniverseShape | null {
  const bare = soleBrandSlug(predicate);
  if (bare !== null) return { slug: bare, narrow: null };
  if (typeof predicate === 'boolean' || predicate.kind !== 'all') return null;
  if (predicate.predicates.length !== 2) return null;

  const [first, second] = predicate.predicates;
  const brandFirst = soleBrandSlug(first);
  if (brandFirst !== null) return narrowedShape(brandFirst, second);
  const brandSecond = soleBrandSlug(second);
  if (brandSecond !== null) return narrowedShape(brandSecond, first);
  return null;
}

// A boolean arm resolves the whole catalog and a second brand-in narrows the brand to a
// different brand's SKUs: both parse as "a brand narrowed by a predicate" but answer
// something the doc does not promise, so neither is served (D-03).
function narrowedShape(slug: string, narrow: SkuPredicate): AvailabilityUniverseShape | null {
  if (typeof narrow === 'boolean' || narrow.kind === 'brand-in') return null;
  return { slug, narrow };
}

// The one slug of a single-slug brand-in predicate; null for every other shape.
function soleBrandSlug(predicate: SkuPredicate): string | null {
  if (typeof predicate === 'boolean' || predicate.kind !== 'brand-in') return null;
  return predicate.slugs.length === 1 ? predicate.slugs[0] : null;
}

// Names an inline universe's shape for the rejection message, a composite down to its arms.
function describeUniverseShape(predicate: SkuPredicate): string {
  if (typeof predicate === 'boolean') return `literal:${predicate}`;
  if (predicate.kind !== 'all') return `predicate:${predicate.kind}`;
  return `predicate:all[${predicate.predicates.map(describeArm).join(', ')}]`;
}

function describeArm(predicate: SkuPredicate): string {
  return typeof predicate === 'boolean' ? `literal:${predicate}` : predicate.kind;
}

// ─── Partitioning ─────────────────────────────────────────────────────────────

// Groups the read's facts by EAN, keeping the weeks whose Monday lies inside the window
// range (inclusive). The feed is already one row per week keyed by that Monday, so weekly
// here means one bucket per feed week — no re-bucketing through isoWeekKey. Decode failures
// are reported by the ACL, so they are dropped rather than reported a second time.
function partitionAvailabilityByEan(
  results: readonly AvailabilityDecodeResult[],
  win: MeasurementWindow
): ReadonlyMap<EAN, readonly AvailabilityObservation[]> {
  const partitions = new Map<EAN, AvailabilityObservation[]>();
  for (const result of results) {
    if (result.kind !== 'ok') continue;
    const { observation } = result;
    if (observation.week < win.range.from || observation.week > win.range.to) continue;
    const facts = partitions.get(observation.ean);
    if (facts === undefined) partitions.set(observation.ean, [observation]);
    else facts.push(observation);
  }
  return partitions;
}
